import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { getJson, postJson } from '../utils/shopApi';
import { GST_STATE_NAMES } from '../utils/countriesStates';
import TaxInvoice from './TaxInvoice';
import PhoneNumberInput, { isValidPhoneNumber, toE164 } from './PhoneNumberInput';

// Each state once, in name order (a few states have two GST codes)
const STATES = [...new Set(Object.values(GST_STATE_NAMES))].filter((n) => !['Other Territory', 'Centre Jurisdiction', 'Daman and Diu'].includes(n)).sort();

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) { resolve(true); return; }
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

/** A quantity with minus and plus buttons; the number can still be typed. */
export function QtyStepper({ value, onChange, size, label = 'Quantity' }) {
  const n = Math.max(1, Math.floor(Number(value)) || 1);
  return (
    <div className={`qty-stepper ${size === 'sm' ? 'sm' : ''}`}>
      <button type="button" aria-label={`Decrease ${label.toLowerCase()}`} disabled={n <= 1} onClick={() => onChange(n - 1)}>&minus;</button>
      <input type="number" min="1" max="1000" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} />
      <button type="button" aria-label={`Increase ${label.toLowerCase()}`} disabled={n >= 1000} onClick={() => onChange(n + 1)}>+</button>
    </div>
  );
}

/** Add one of a product to the cart (or pick a quantity on the product page). */
export function AddToCart({ cart, productId, inStock, home, withQuantity = false }) {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  if (!inStock) return null;
  const add = () => {
    cart.add(productId, Math.max(1, Math.floor(Number(qty)) || 1));
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };
  return (
    <div data-testid="add-to-cart">
      <div className="d-flex align-items-stretch gap-2">
        {withQuantity && <QtyStepper value={qty} onChange={setQty} />}
        <Button size={withQuantity ? 'lg' : 'sm'} className={withQuantity ? 'flex-grow-1' : 'w-100'} variant={added ? 'success' : 'primary'} onClick={add}>
          <i className={`bi ${added ? 'bi-check2' : 'bi-bag-plus'} me-1`} />{added ? 'Added' : 'Add to cart'}
        </Button>
      </div>
      {withQuantity && added && <div className="mt-2"><Link to={`${home}/cart`} className="btn btn-outline-primary btn-sm">View cart <i className="bi bi-arrow-right" /></Link></div>}
    </div>
  );
}

/** What the server says the cart costs now: lines (with any problem), GST, shipping, total. */
function useQuote(base, items, state) {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const signature = JSON.stringify([items, state || '']);
  useEffect(() => {
    if (items.length === 0) { setQuote(null); return undefined; }
    let live = true;
    postJson(`${base}/cart/quote`, { items, state: state || null })
      .then((q) => { if (live) { setQuote(q); setError(null); } })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, signature]);
  return { quote, error };
}

function Totals({ quote, money }) {
  const row = (label, value, strong) => <div className={`d-flex justify-content-between ${strong ? 'fw-bold fs-5 border-top pt-2 mt-2' : 'mb-1'}`}><span className={strong ? '' : 'text-muted'}>{label}</span><span>{value}</span></div>;
  return (
    <div>
      {row('Items (before GST)', money(quote.taxable))}
      {Number(quote.tax) > 0 && (quote.intraState
        ? (<>{row('CGST', money(quote.cgst))}{row('SGST', money(quote.sgst))}</>)
        : row('IGST', money(quote.igst)))}
      {row('Shipping', Number(quote.shipping) > 0 ? money(quote.shipping) : 'Free')}
      {row('Total', money(quote.total), true)}
    </div>
  );
}

export function CartView({ base, home, cart, money }) {
  const navigate = useNavigate();
  const { quote, error } = useQuote(base, cart.items);

  if (cart.items.length === 0) {
    return (
      <div className="shop-card text-center py-5">
        <i className="bi bi-bag fs-1 text-muted" />
        <p className="text-muted mt-2">Your cart is empty.</p>
        <Link to={home} className="btn btn-primary">Continue shopping</Link>
      </div>
    );
  }
  if (error) return <Alert variant="warning">{error}</Alert>;
  if (!quote) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  return (
    <div data-testid="shop-cart">
      <h1 className="h4 mb-3">Your cart</h1>
      <Row className="g-4">
        <Col lg={8}>
          <div className="shop-card">
            {quote.lines.map((l, i) => (
              <div key={l.productId} className={`d-flex align-items-center flex-wrap gap-3 p-3 ${i > 0 ? 'border-top' : ''}`}>
                <div className="flex-grow-1" style={{ minWidth: 160 }}>
                  {l.problem ? <span className="fw-semibold">{l.name}</span> : <Link to={`${home}/product/${l.productId}`} className="fw-semibold text-decoration-none">{l.name}</Link>}
                  {l.unitPrice != null && <div className="small text-muted">{money(l.unitPrice)} each{Number(l.gstRate) > 0 ? ` + ${Number(l.gstRate)}% GST` : ''}</div>}
                  {l.problem && <div className="small text-danger">{l.problem}</div>}
                </div>
                <QtyStepper size="sm" value={l.quantity} onChange={(v) => cart.setQuantity(l.productId, v)} label={`Quantity of ${l.name}`} />
                <div className="fw-semibold text-end" style={{ minWidth: 96 }}>{l.lineTotal != null ? money(l.lineTotal) : ''}</div>
                <Button variant="outline-secondary" size="sm" aria-label={`Remove ${l.name}`} title="Remove" onClick={() => cart.remove(l.productId)}><i className="bi bi-trash" /></Button>
              </div>
            ))}
          </div>
          <Link to={home} className="back-link d-inline-block mt-3"><i className="bi bi-arrow-left" /> Continue shopping</Link>
        </Col>
        <Col lg={4}>
          <div className="shop-card p-4 sticky-summary">
            <h2 className="h6 mb-3">Order summary</h2>
            <Totals quote={quote} money={money} />
            {quote.problems.length > 0 && <Alert variant="warning" className="small mt-3 mb-0">{quote.problems.map((p) => <div key={p}>{p}</div>)}</Alert>}
            <Button size="lg" className="w-100 mt-3" disabled={!quote.ok} onClick={() => navigate(`${home}/checkout`)} data-testid="go-checkout">Checkout</Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}

/** The checkout field a refusal from the server is about, from its wording */
const fieldFor = (m) => {
  const t = m || '';
  if (/phone/i.test(t)) return 'phone';
  if (/email/i.test(t)) return 'email';
  if (/gstin/i.test(t)) return 'gstin';
  if (/\bpin\b|postal/i.test(t)) return 'postalCode';
  if (/address/i.test(t)) return 'address';
  return null;
};

const EMPTY = { type: 'CONSUMER', name: '', email: '', phone: '', businessName: '', gstin: '', address: '', city: '', state: '', postalCode: '', paymentMethod: '', note: '', consent: false, website: '' };

export function CheckoutView({ base, home, cart, info, money, trade }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [badField, setBadField] = useState(null);
  const { quote, error } = useQuote(base, cart.items, form.state);
  const methods = useMemo(() => [
    trade?.canBuyOnCredit && ['CREDIT', `On my credit terms (${trade.creditDays} days)`],
    info.payments?.online && ['ONLINE', 'Pay now: card, UPI, net banking or wallet'],
    info.payments?.upi && ['UPI', 'UPI'], info.payments?.bankTransfer && ['BANK_TRANSFER', 'Bank transfer'], info.payments?.cod && ['COD', 'Cash on delivery']].filter(Boolean), [info, trade]);

  // A trade customer's details come from their record: the buyer only adds who is ordering and where it goes
  useEffect(() => {
    if (!trade) return;
    const state = STATES.find((s) => s.toLowerCase() === String(trade.state || '').toLowerCase()) || '';
    setForm((f) => (f.name || f.address ? f : {
      ...f, type: 'BUSINESS', name: trade.contactName || '', email: trade.email || '', phone: trade.phone || '', address: trade.address || '', city: trade.city || '',
      state, postalCode: trade.postalCode || '', businessName: trade.name, gstin: trade.gstin || ''
    }));
  }, [trade]);
  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    if (badField === k) setBadField(null);
  };

  useEffect(() => {
    if (!form.paymentMethod && methods.length === 1) setForm((f) => ({ ...f, paymentMethod: methods[0][0] }));
  }, [methods, form.paymentMethod]);

  if (cart.items.length === 0) return <div className="text-center py-5"><p className="text-muted">Your cart is empty.</p><Link to={home}>Continue shopping</Link></div>;
  if (error) return <Alert variant="warning">{error}</Alert>;
  if (!quote) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  // Razorpay's checkout window, with the merchant's own key; the order exists only after the payment is confirmed
  const payNow = async (buyer) => {
    const started = await postJson(`${base}/payments/start`, { items: cart.items, buyer });
    const loaded = await loadRazorpayScript();
    if (!loaded || !window.Razorpay) throw new Error('The payment window could not be opened. Please check your connection and try again.');
    await new Promise((resolve, reject) => {
      const rz = new window.Razorpay({
        key: started.keyId,
        amount: Math.round(Number(started.amount) * 100),
        currency: started.currency,
        order_id: started.razorpayOrderId,
        name: started.name,
        description: 'Your order',
        prefill: started.prefill,
        handler: async (response) => {
          try {
            const done = await postJson(`${base}/payments/confirm`, {
              razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature
            });
            cart.clear();
            navigate(done.token ? `${home}/order/${done.token}` : `${home}`, { replace: true });
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        modal: { ondismiss: () => reject(new Error('The payment was not completed. You have not been charged; you can try again.')) }
      });
      rz.on('payment.failed', (r) => reject(new Error(r?.error?.description || 'The payment failed. You can try again.')));
      rz.open();
    });
  };

  // Marks the field a message is about and takes the buyer to it, instead of leaving a red box at the top of the page
  const flag = (field, message) => {
    setProblem(message);
    setBadField(field);
    const box = field && document.getElementById(`co-${field}`);
    if (box) {
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      box.focus({ preventScroll: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setProblem(null);
    setBadField(null);
    const phone = toE164(form.phone);
    if (!isValidPhoneNumber(phone || '')) {
      flag('phone', 'Enter a valid phone number, with the country selected, so the seller can reach you about this order.');
      return;
    }
    setBusy(true);
    try {
      if (form.paymentMethod === 'ONLINE') {
        await payNow({ ...form, phone });
        return;
      }
      const r = await postJson(`${base}/orders`, { items: cart.items, buyer: { ...form, phone } });
      cart.clear();
      navigate(`${home}/order/${r.token}`, { replace: true });
    } catch (err) {
      flag(fieldFor(err.message), err.message);
    } finally {
      setBusy(false);
    }
  };

  const business = form.type === 'BUSINESS';
  return (
    <Form onSubmit={submit} data-testid="shop-checkout">
      <h1 className="h4 mb-3">Checkout</h1>
      {problem && <Alert variant="danger" data-testid="checkout-problem">{problem}</Alert>}
      <Row className="g-4">
        <Col lg={7}>
          <div className="shop-card p-4 mb-3">
          <h2 className="section-title"><span className="step">1</span>Your details</h2>
          {trade && (
            <Alert variant="light" className="border py-2 small" data-testid="trade-buyer">
              Ordering for <strong>{trade.name}</strong>{trade.gstin ? ` (GSTIN ${trade.gstin})` : ''} at your trade prices. Change the person or delivery address if this order goes elsewhere.
            </Alert>
          )}
          <div className="mb-3" hidden={Boolean(trade)}>
            <Form.Check inline type="radio" name="type" id="t-consumer" label="Buying for myself" checked={!business} onChange={() => setForm((f) => ({ ...f, type: 'CONSUMER' }))} />
            <Form.Check inline type="radio" name="type" id="t-business" label="Buying for a business" checked={business} onChange={() => setForm((f) => ({ ...f, type: 'BUSINESS' }))} />
          </div>
          <Row className="g-3">
            {business && !trade && (
              <>
                <Col md={7}><Form.Group><Form.Label>Business name</Form.Label><Form.Control value={form.businessName} onChange={set('businessName')} maxLength={160} required /></Form.Group></Col>
                <Col md={5}><Form.Group><Form.Label>GSTIN (for a tax invoice)</Form.Label><Form.Control id="co-gstin" isInvalid={badField === 'gstin'} value={form.gstin} onChange={set('gstin')} maxLength={15} /></Form.Group></Col>
              </>
            )}
            <Col md={6}><Form.Group><Form.Label>Your name</Form.Label><Form.Control id="co-name" isInvalid={badField === 'name'} value={form.name} onChange={set('name')} maxLength={120} required autoComplete="name" /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Phone</Form.Label><PhoneNumberInput id="co-phone" name="phone" value={form.phone} onChange={set('phone')} required invalid={badField === 'phone'} /></Form.Group></Col>
            <Col md={12}><Form.Group><Form.Label>Email (your order link is sent here)</Form.Label><Form.Control id="co-email" isInvalid={badField === 'email'} type="email" value={form.email} onChange={set('email')} maxLength={160} required autoComplete="email" /></Form.Group></Col>
          </Row>
          </div>
          <div className="shop-card p-4 mb-3">
          <h2 className="section-title"><span className="step">2</span>Delivery</h2>
          <Row className="g-3">
            <Col md={12}><Form.Group><Form.Label>Delivery address</Form.Label><Form.Control id="co-address" isInvalid={badField === 'address'} as="textarea" rows={2} value={form.address} onChange={set('address')} maxLength={300} required autoComplete="street-address" /></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>City</Form.Label><Form.Control value={form.city} onChange={set('city')} maxLength={100} required /></Form.Group></Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>State</Form.Label>
                <Form.Select value={form.state} onChange={set('state')} required data-testid="checkout-state">
                  <option value="">Choose a state</option>
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}><Form.Group><Form.Label>PIN code</Form.Label><Form.Control id="co-postalCode" isInvalid={badField === 'postalCode'} value={form.postalCode} onChange={set('postalCode')} maxLength={12} autoComplete="postal-code" /></Form.Group></Col>
            <Col md={12}><Form.Group><Form.Label>Note for the seller (optional)</Form.Label><Form.Control value={form.note} onChange={set('note')} maxLength={500} /></Form.Group></Col>
          </Row>
          </div>
          <div className="shop-card p-4 mb-3">
          <h2 className="section-title"><span className="step">3</span>How will you pay?</h2>
          {methods.map(([key, label]) => (
            <div key={key} className={`pay-option ${form.paymentMethod === key ? 'selected' : ''}`}>
              <Form.Check type="radio" name="pay" id={`pay-${key}`} label={label} checked={form.paymentMethod === key} onChange={() => setForm((f) => ({ ...f, paymentMethod: key }))} />
            </div>
          ))}
          {info.payments?.note && <div className="small text-muted mt-1">{info.payments.note}</div>}
          <div className="small text-muted mt-1">{form.paymentMethod === 'CREDIT' ? 'This goes on your account: there is nothing to pay now.' : form.paymentMethod === 'ONLINE' ? 'You pay in a secure Razorpay window. Your order is placed once the payment goes through.' : 'You pay after placing the order; the next page shows how.'}</div>

          {/* a field only a robot fills in */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px' }}>
            <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></label>
          </div>
          <Form.Check className="mt-4" type="checkbox" id="consent" checked={form.consent} onChange={set('consent')} required data-testid="checkout-consent"
            label={<>I agree to the <Link to={`${home}/policies`} target="_blank">shipping, returns and terms</Link> of {info.name}.</>} />
          </div>
        </Col>
        <Col lg={5}>
          <div className="shop-card p-4 sticky-summary">
            <h2 className="h6 mb-3">Your order</h2>
            {quote.lines.map((l) => (
              <div key={l.productId} className="d-flex justify-content-between small mb-1">
                <span>{l.name} &times; {l.quantity}{l.problem && <span className="text-danger"> ({l.problem})</span>}</span><span>{l.lineTotal != null ? money(l.lineTotal) : ''}</span>
              </div>
            ))}
            <hr />
            <Totals quote={quote} money={money} />
            {!form.state && Number(quote.tax) > 0 && <div className="small text-muted mt-2">Choose your state to see how the GST is shown.</div>}
            {!form.state && quote.shippingByWeight && <div className="small text-muted mt-2" data-testid="shipping-estimate">Shipping depends on your weight and state: choose your state for the exact charge.</div>}
            {quote.problems.length > 0 && <Alert variant="warning" className="small mt-3 mb-0">{quote.problems.map((p) => <div key={p}>{p}</div>)} <Link to={`${home}/cart`}>Change cart</Link></Alert>}
            <Button type="submit" size="lg" className="w-100 mt-3" disabled={busy || !quote.ok} data-testid="place-order">{busy ? (form.paymentMethod === 'ONLINE' ? 'Waiting for the payment...' : 'Placing your order...') : form.paymentMethod === 'ONLINE' ? `Pay ${money(quote.total)}` : `Place order · ${money(quote.total)}`}</Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
}

const STATUS_TEXT = { PENDING: 'Received', CONFIRMED: 'Confirmed', PROCESSING: 'Being prepared', SHIPPED: 'Shipped', DELIVERED: 'Delivered', RETURNED: 'Returned', CANCELLED: 'Cancelled' };
const STEPS = ['Received', 'Confirmed', 'Shipped', 'Delivered'];
const STEP_AT = { PENDING: 0, CONFIRMED: 1, PROCESSING: 1, SHIPPED: 2, DELIVERED: 3 };
const PAYMENT_TEXT = { PENDING: 'Payment due', PAID: 'Paid', PARTIAL: 'Part paid', REFUNDED: 'Refunded' };

/** The buyer's order, opened from the link in their email: what was ordered, where it stands, and how to pay. */
/** The buyer's own tax invoice, opened with their order link: to read, print or save as a PDF. */
export function InvoiceView({ base, home, token }) {
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    getJson(`${base}/orders/${encodeURIComponent(token)}/invoice`).then(setInv).catch((e) => setError(e.message));
  }, [base, token]);
  if (error) return <Alert variant="warning">{error} <Link to={`${home}/order/${token}`}>Back to your order</Link></Alert>;
  if (!inv) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  return (
    <div data-testid="shop-invoice">
      <style>{'@media print { [data-testid="shop"] header, [data-testid="shop"] footer, .no-print { display: none !important; } }'}</style>
      <div className="no-print d-flex align-items-center gap-3 mb-3">
        <Link to={`${home}/order/${token}`} className="back-link"><i className="bi bi-arrow-left" /> Back to your order</Link>
        <Button size="sm" onClick={() => window.print()} data-testid="print-invoice"><i className="bi bi-printer me-1" />Print or save as PDF</Button>
      </div>
      <TaxInvoice data={inv} forBuyer />
    </div>
  );
}

export function OrderView({ base, home, token, money }) {
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [cancelProblem, setCancelProblem] = useState(null);
  useEffect(() => {
    getJson(`${base}/orders/${encodeURIComponent(token)}`).then(setOrder).catch((e) => setError(e.message));
  }, [base, token]);

  const cancelOrder = async (e) => {
    e.preventDefault();
    setBusy(true);
    setCancelProblem(null);
    try {
      setOrder(await postJson(`${base}/orders/${encodeURIComponent(token)}/cancel`, { reason }));
      setCancelling(false);
    } catch (err) {
      setCancelProblem(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert variant="warning">{error} <Link to={home}>Back to the store</Link></Alert>;
  if (!order) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  const pay = order.pay;
  const cod = order.paymentMethod === 'COD';
  const credit = Boolean(order.onCredit);
  const at = STEP_AT[order.status];

  return (
    <div style={{ maxWidth: 820 }} data-testid="shop-order">
      <div className="shop-card p-4 mb-3">
      <h1 className="h4">Thank you, {order.buyerName}</h1>
      <p className="text-muted">Order <strong>{order.orderNumber}</strong>, placed {when(order.placedAt)}. Keep this page's link to see how your order is going.</p>
      {at !== undefined && (
        <ol className="order-steps my-3" aria-label="Order progress">
          {STEPS.map((label, i) => (
            <li key={label} className={i < at ? 'done' : i === at ? 'current' : ''}>
              <span className="dot">{i < at ? <i className="bi bi-check-lg" /> : i + 1}</span>
              <div>{label}</div>
            </li>
          ))}
        </ol>
      )}
      <div>
        <Badge bg={order.status === 'CANCELLED' ? 'danger' : order.status === 'DELIVERED' ? 'success' : 'primary'} className="me-2">{STATUS_TEXT[order.status] || order.status}</Badge>
        <Badge bg={order.paymentStatus === 'PAID' ? 'success' : 'warning'} text={order.paymentStatus === 'PAID' ? undefined : 'dark'}>{credit && order.paymentStatus !== 'PAID' ? 'On credit terms' : cod && order.paymentStatus !== 'PAID' ? 'Pay on delivery' : PAYMENT_TEXT[order.paymentStatus] || order.paymentStatus}</Badge>
      </div>
      </div>

      {order.invoiceNumber && (
        <div className="shop-card p-3 mb-3 d-flex align-items-center flex-wrap gap-3" data-testid="order-invoice">
          <i className="bi bi-receipt fs-3 text-primary" />
          <div className="flex-grow-1">Your tax invoice <strong>{order.invoiceNumber}</strong> is ready. You can print it or save it as a PDF.</div>
          <Link to={`${home}/invoice/${token}`} className="btn btn-primary btn-sm">View tax invoice</Link>
        </div>
      )}

      {pay && (
        <div className="shop-card p-4 mb-3" data-testid="order-pay">
          <h2 className="h6">How to pay {money(order.total)}</h2>
          {order.payBy && <Alert variant="warning" className="py-2 small" data-testid="order-pay-by">Please pay by <strong>{when(order.payBy)}</strong>. An order not paid by then is cancelled.</Alert>}
          {pay.upiLink && (
            <div className="d-flex flex-wrap gap-4 align-items-center">
              <div className="bg-white p-2 rounded"><QRCodeSVG value={pay.upiLink} size={140} /></div>
              <div>
                <div>Scan with any UPI app, or</div>
                <a className="btn btn-primary btn-sm my-2" href={pay.upiLink}>Pay with a UPI app</a>
                <div className="small text-muted">UPI ID: {pay.upiId}. Mention {order.orderNumber}.</div>
              </div>
            </div>
          )}
          {pay.bank && (
            <Table size="sm" borderless className="mb-0 w-auto">
              <tbody>
                <tr><td className="text-muted">Account name</td><td>{pay.bank.accountName}</td></tr>
                <tr><td className="text-muted">Bank</td><td>{pay.bank.bankName}{pay.bank.branch ? `, ${pay.bank.branch}` : ''}</td></tr>
                <tr><td className="text-muted">Account number</td><td>{pay.bank.accountNumber}</td></tr>
                <tr><td className="text-muted">IFSC</td><td>{pay.bank.ifsc}</td></tr>
              </tbody>
            </Table>
          )}
          {!pay.upiLink && !pay.bank && <div>The seller will contact you with payment details.</div>}
          {pay.note && <div className="small mt-2">{pay.note}</div>}
          <div className="small text-muted mt-2">The seller marks your order paid when the money arrives.</div>
        </div>
      )}
      {credit && order.paymentStatus !== 'PAID' && order.status !== 'CANCELLED' && <Alert variant="info" data-testid="order-credit">This order is on your credit terms: there is nothing to pay now. {money(order.total)} goes on your account.</Alert>}
      {cod && order.paymentStatus !== 'PAID' && order.status !== 'CANCELLED' && <Alert variant="info">You will pay {money(order.total)} in cash when your order is delivered.</Alert>}

      {order.courierName && (
        <Alert variant="success" data-testid="order-courier">
          Sent with <strong>{order.courierName}</strong>{order.shippedAt ? ` on ${when(order.shippedAt)}` : ''}.
          {order.trackingNumber && <> Tracking number: <strong>{order.trackingNumber}</strong>.</>}
          {order.trackingUrl && <> <a href={order.trackingUrl} target="_blank" rel="noreferrer">Track your order</a></>}
        </Alert>
      )}

      <div className="shop-card p-4 mb-3">
      <h2 className="h6">Items</h2>
      <Table size="sm" responsive borderless>
        <tbody>
          {order.lines.map((l, i) => <tr key={`${l.name}-${i}`}><td>{l.name} &times; {l.quantity}</td><td className="text-end">{money(l.lineTotal)}</td></tr>)}
          {Number(order.shipping) > 0 && <tr><td>Shipping</td><td className="text-end">{money(order.shipping)}</td></tr>}
          <tr className="fw-bold"><td>Total (including GST)</td><td className="text-end">{money(order.total)}</td></tr>
        </tbody>
      </Table>
      <h2 className="h6 mt-3">Delivering to</h2>
      <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{order.deliverTo}</p>
      </div>
      {order.canCancel && (
        <div className="mb-3" data-testid="order-cancel">
          {!cancelling ? (
            <Button variant="outline-danger" size="sm" onClick={() => setCancelling(true)}>Cancel my order</Button>
          ) : (
            <Form onSubmit={cancelOrder} className="border rounded p-3">
              <Form.Group className="mb-2">
                <Form.Label>Why are you cancelling? (optional)</Form.Label>
                <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
              </Form.Group>
              {cancelProblem && <Alert variant="danger" className="py-2 small">{cancelProblem}</Alert>}
              <Button type="submit" variant="danger" size="sm" disabled={busy}>{busy ? 'Cancelling...' : 'Yes, cancel this order'}</Button>{' '}
              <Button variant="link" size="sm" onClick={() => setCancelling(false)}>Keep my order</Button>
            </Form>
          )}
          <div className="small text-muted mt-1">You can cancel until the seller confirms your order.</div>
        </div>
      )}
      {order.status === 'CANCELLED' && <Alert variant="secondary" data-testid="order-cancelled">This order is cancelled.</Alert>}
      {(order.contactPhone || order.contactEmail) && <p className="small text-muted">Questions? Contact {order.storeName}: {[order.contactPhone, order.contactEmail].filter(Boolean).join(' · ')}</p>}
    </div>
  );
}
